import { faFileInvoice } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl, FormattedMessage } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import { cardMessages } from 'src/features/cards/utils/messages';
import PaymentDetailsButtons from 'src/features/statistics/components/PaymentDetailsButtons';
import { useGetDigitalReceipt } from 'src/features/statistics/hooks/useGetDigitalReceipt';
import { useGetOnlinePaymentReceipt } from 'src/features/statistics/hooks/useGetOnlinePaymentReceipt';
import { statisticsMessages } from 'src/features/statistics/utils';
import { useParking } from 'src/providers/ParkingProvider';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { cn } from 'src/utils';
import { getFormattedDateTime, getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { generalMessages } from 'src/utils/messages';

import type { PaymentDetailsData, StatisticPaymentDetails } from 'src/features/statistics/types';

type PaymentDetailsProps = {
    paymentDetails: StatisticPaymentDetails | undefined;
};

const ONLINE_PAYMENT_METHODS = ['Онлайн плащане', 'Онлайн'];

const PaymentDetails = (props: PaymentDetailsProps) => {
    const { paymentDetails } = props;
    const { formatMessage, formatNumber } = useIntl();

    const { parkingId } = useParking();

    const isOnlinePayment = paymentDetails?.payment_type_name
        ? ONLINE_PAYMENT_METHODS.includes(paymentDetails?.payment_type_name)
        : false;

    const { data: fiscalNote } = useGetDigitalReceipt({
        parkingId,
        fiscalNoteId: paymentDetails?.unp_by_fiscal || '',
        config: { enabled: !isOnlinePayment && !!paymentDetails?.unp_by_fiscal },
    });
    const { data: onlinePayments } = useGetOnlinePaymentReceipt({
        parkingId,
        paymentOrderNumber: paymentDetails?.unp_by_fiscal || '',
        config: { enabled: isOnlinePayment && !!paymentDetails?.unp_by_fiscal },
    });

    const { formattedDate } = getFormattedDateTime(paymentDetails?.payment_datetime);

    const sidePanelBodyAttributeMapping: PaymentDetailsData[] = [
        {
            title: formatMessage(cardMessages.cardNumber),
            value: paymentDetails?.identifier,
            control: paymentDetails,
        },
        {
            title: formatMessage(statisticsMessages.date),
            value: getFormattedDateTimeForDetailPanel(paymentDetails?.payment_datetime) || '-',
            control: paymentDetails,
        },
        {
            title: formatMessage(statisticsMessages.category),
            value: paymentDetails?.item_name,
            control: paymentDetails,
        },
        {
            title: formatMessage(cardMessages.paymentMethod),
            value: paymentDetails?.payment_type_name,
            control: paymentDetails,
        },
        {
            title: formatMessage(statisticsMessages.quantity),
            value: paymentDetails?.item_quantity,
            control: paymentDetails,
        },
        {
            title: formatMessage(statisticsMessages.withoutVat),
            value: formatNumber(paymentDetails?.price_no_vat / 100, {
                style: 'currency',
                currency: paymentDetails?.currency,
            }),
            control: paymentDetails,
        },
        {
            title: formatMessage(cardMessages.vatAmount),
            value: formatNumber(paymentDetails?.price_vat_only / 100, {
                style: 'currency',
                currency: paymentDetails?.currency,
            }),
            control: paymentDetails,
        },
        {
            title: formatMessage(statisticsMessages.withVat),
            value: formatNumber(paymentDetails?.price_total / 100, {
                style: 'currency',
                currency: paymentDetails?.currency,
            }),
            control: paymentDetails,
        },
        {
            title: formatMessage(statisticsMessages.fiscalNoteNumber),
            value: paymentDetails?.unp_by_fiscal,
            control: paymentDetails,
        },
        {
            title: formatMessage(cardMessages.cardTypeOperator),
            value: paymentDetails?.operator_name,
            control: paymentDetails,
        },
        {
            title: formatMessage(statisticsMessages.workingPlace),
            value: paymentDetails?.working_place_name,
            control: paymentDetails,
        },
        {
            title: formatMessage(statisticsMessages.eventType),
            value: paymentDetails?.event_name,
            control: paymentDetails,
        },
    ];

    return (
        <>
            {paymentDetails ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{paymentDetails.identifier}</DialogTitle>
                    </div>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                </DialogHeader>
            )}
            <DialogSeparator />
            <DialogBody>
                <div className="aspect-video min-h-44 w-full rounded-lg border p-1">
                    <div className={cn('flex size-full items-center justify-center rounded-md bg-gray-100')}>
                        <FontAwesomeIcon icon={faFileInvoice} {...props} className="size-20 text-black/50" />
                    </div>
                </div>
                <PaymentDetailsButtons
                    paymentDate={formattedDate}
                    digitalReceiptDetails={isOnlinePayment ? onlinePayments?.details : fiscalNote?.details || ''}
                />
                <div className="mt-4 grid gap-2">
                    {sidePanelBodyAttributeMapping.map((details: PaymentDetailsData) => {
                        return (
                            <SidePanelBodyAttribute key={details.title}>
                                <SidePanelBodyAttributeTitle>{details.title}</SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={details.control}>
                                    {details.value}
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>
                        );
                    })}
                </div>
            </DialogBody>
            <DialogSeparator />
            <DialogFooter className="flex flex-col gap-2 sm:flex-col">
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        <FormattedMessage {...generalMessages.close} />
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default PaymentDetails;
